import { type AxiosInstance, HttpStatusCode, isAxiosError } from "axios";
import { SendToTrackingService } from "@/api/handlers/errors";

/**
 * 不動産検索の土地の住居表示を取得した結果。
 *
 * - found: 取得できた（住居表示が無い座標は address が空文字）
 * - rateLimited: ユーザー単位の回数制限（429）。少し待てば取れる
 * - unavailable: Google の回数制限などで BE が取れなかった（503）。時間をおいて再試行できる
 * - forbidden: 地図サービスの契約がない（403）。再試行しても取れない
 * - error: それ以外（通信エラー・500 など）
 */
export type RealEstateSearchAddressResult =
  | { kind: "found"; address: string }
  | { kind: "rateLimited" }
  | { kind: "unavailable" }
  | { kind: "forbidden" }
  | { kind: "error" };

interface GetRealEstateSearchAddressAPIResponseBody {
  address: string;
}

/**
 * 不動産検索で住居表示を空で返した土地について、ピンの座標から住居表示を1件取得する。
 * lat/lng には検索結果の latitude/longitude をそのまま渡す
 */
export const getRealEstateSearchAddressAPI = async (
  apiClient: AxiosInstance,
  lat: number,
  lng: number
): Promise<RealEstateSearchAddressResult> => {
  try {
    const response =
      await apiClient.get<GetRealEstateSearchAddressAPIResponseBody>(
        "map/real-estate-search/address",
        { params: { lat, lng } }
      );
    return { kind: "found", address: response.data.address ?? "" };
  } catch (error) {
    if (!isAxiosError(error)) return { kind: "error" };
    const status = error.response?.status;
    if (status === HttpStatusCode.TooManyRequests) {
      return { kind: "rateLimited" };
    }
    // 503 は Google の回数制限など一時的なもので、BE がリトライ済み。障害として送らない
    if (status === HttpStatusCode.ServiceUnavailable) {
      return { kind: "unavailable" };
    }
    if (status === HttpStatusCode.Forbidden) return { kind: "forbidden" };
    if ((status ?? HttpStatusCode.InternalServerError) >= 500) {
      SendToTrackingService(error);
    }
    return { kind: "error" };
  }
};
